import React, { useState, useEffect } from 'react';
import { Eye, Trash2 } from 'lucide-react';
import { DataTable, Column } from '@components';
import { useAdminReservations } from '../../../hooks/useAdminReservations';
import { ReservationResponseDto } from '@types';
import RESERVATION_STATUS_OPTIONS, { STATUS_VISUALS } from '@constants/reservationStatus';
import { userService } from '../../../services/userService';
import { equipmentService } from '../../../services/equipmentService';
import { IconSelect } from '@components/atoms/IconSelect';
import ReservationModal from '../../../components/atoms/ReservationModal';

// formateador de fecha simple
import { Icon } from '@icons';
const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return iso;
  }
};
import { reservationService } from '../../../services/reservationService';

export const BookingsTab: React.FC = () => {
  const {
    data: reservations,
    page,
    limit,
    total,
    loading,
    setPage,
    setLimit,
    setStatusFilter,
    statusFilter,
    setSearchTerm,
    searchTerm,
    refresh,
    performStatusTransition,
  } = useAdminReservations({ initialLimit: 10 });

  // cache local de usuarios y equipos
  const [usersMap, setUsersMap] = useState<Record<string, { id: string; name: string; phone?: string; shippingAddress?: string }>>({});
  const [equipMap, setEquipMap] = useState<Record<string, { id: string; name: string }>>({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const users = await userService.getAllUsers();
        if (!mounted) return;
        const map: Record<string, { id: string; name: string; phone?: string; shippingAddress?: string }> = {};
        users.forEach(u => (map[u.id] = { id: u.id, name: u.name, phone: u.phone, shippingAddress: u.shippingAddress }));
        setUsersMap(map);
      } catch (err) {
        console.error('Error cargando usuarios:', err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // cuando cambian las reservas cargadas, asegurar nombres de equipment en caché
  useEffect(() => {
    const idsToLoad = Array.from(new Set(reservations.map(r => r.equipmentId).filter(Boolean)));
    idsToLoad.forEach(id => {
      if (!equipMap[id]) {
        equipmentService.getEquipmentById(id).then(e => {
          setEquipMap(prev => ({ ...prev, [id]: { id, name: e?.name || id } }));
        }).catch(() => {
          setEquipMap(prev => ({ ...prev, [id]: { id, name: id } }));
        });
      }
    });
  }, [reservations, equipMap]);

  // cuando cambian las reservas cargadas, asegurar nombres de usuario en caché
  useEffect(() => {
    const userIds = Array.from(new Set(reservations.map(r => r.userId).filter(Boolean)));
    userIds.forEach(uid => {
      if (!usersMap[uid]) {
        userService.getUserById(uid).then(u => {
          setUsersMap(prev => ({ ...prev, [uid]: { id: uid, name: u?.name || uid, phone: u?.phone, shippingAddress: u?.shippingAddress } }));
        }).catch(() => {
          setUsersMap(prev => ({ ...prev, [uid]: { id: uid, name: uid } }));
        });
      }
    });
  }, [reservations, usersMap]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Modal state
  const [openReservation, setOpenReservation] = useState<ReservationResponseDto | null>(null);

  const openModal = (r: ReservationResponseDto) => {
    setOpenReservation(r);
  };

  const closeModal = () => setOpenReservation(null);

  const handleCancel = async (id: string) => {
    if (!confirm('¿Estás seguro de que deseas cancelar esta reserva?')) return;
    try {
      setDeletingId(id);
      await reservationService.cancelReservation(id, 'Cancelled by admin');
      refresh();
    } catch (err) {
      console.error('Cancel reservation error', err);
    } finally {
      setDeletingId(null);
    }
  };

  const columns: Column<ReservationResponseDto>[] = [
    {
      key: 'code',
      header: 'Code',
      width: 'w-1/5',
      minWidth: 'min-w-[140px]',
      render: (r) => r.reservationCode,
    },
    {
      key: 'dates',
      header: 'Dates',
      width: 'w-1/5',
      minWidth: 'min-w-[140px]',
      render: (r) => `${formatDate(r.startDate)} → ${formatDate(r.endDate)}`,
    },
    {
      key: 'user',
      header: 'Usuario',
      width: 'w-1/5',
      minWidth: 'min-w-[140px]',
      render: (r) => usersMap[r.userId]?.name || r.userId,
    },
    {
      key: 'equipment',
      header: 'Equipo',
      width: 'w-1/5',
      minWidth: 'min-w-[140px]',
      render: (r) => equipMap[r.equipmentId]?.name || r.equipmentId,
    },
    {
      key: 'status',
      header: 'Estado',
      width: 'w-1/5',
      minWidth: 'min-w-[120px]',
      render: (r) => {
        const opt = RESERVATION_STATUS_OPTIONS.find(o => o.value === (r.status as string));
        const s = STATUS_VISUALS[r.status as string] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'default' };
        const label = opt ? opt.label : (r.status as string);
        const iconName = (opt?.visual?.icon) || s.icon || 'default';
        return (
          <span onClick={() => openModal(r)} className={`cursor-pointer inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text} capitalize`}>
            <span className="mr-2">
              <Icon name={iconName} size={12} className="mr-1" />
            </span>
            {label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      width: 'w-1/5',
      minWidth: 'min-w-[120px]',
      className: 'sticky right-0 bg-white z-10 min-w-[120px] md:static',
      render: (r) => (
        <div className="flex space-x-2">
          <button onClick={() => openModal(r)} className="text-blue-600 cursor-pointer hover:text-blue-900" title="Ver">
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleCancel(r.id)}
            className="text-red-600 cursor-pointer hover:text-red-900"
            title="Cancel"
            disabled={deletingId === r.id}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Gestión de Reservas</h2>
      </div>

      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Buscar por código, usuario o equipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="py-2 pl-3 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="w-64">
            <IconSelect
              value={statusFilter || ''}
              onChange={(v) => setStatusFilter(v || undefined)}
              options={[{ value: '', label: 'Todos', visual: { icon: 'default', color: 'text-gray-600' } }, ...RESERVATION_STATUS_OPTIONS]}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-600">Total: {total}</div>
        </div>
      </div>

      <DataTable
        data={reservations}
        columns={columns}
        emptyMessage="No se encontraron reservas"
        loading={loading}
        loadingMessage="Cargando reservas..."
        className="mb-4"
        tableClassName="sm:table-fixed"
        mobileCard={true}
      />

      <ReservationModal
        open={!!openReservation}
        onClose={closeModal}
        reservation={openReservation}
        user={openReservation ? (usersMap[openReservation.userId] || null) : null}
        equipment={openReservation ? (equipMap[openReservation.equipmentId] || null) : null}
        onStatusChange={async (_id: string, status: string) => {
          try {
            if (!openReservation) throw new Error('Reservation not provided');
            const updated = await performStatusTransition(openReservation, status);
            refresh();
            setOpenReservation(updated);
          } catch (err) {
            console.error('Error actualizando estado (modal):', err);
            throw err;
          }
        }}
      />

      {/* Simple pagination controls */}
      <div className="flex items-center justify-between">
        <div className="space-x-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page * limit >= total}
            className="px-4 py-2 text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <label className="text-sm">Por página:</label>
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="px-2 py-1 border rounded">
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// Modal atom moved to src/components/atoms/ReservationModal.tsx
